import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SortDirection } from '@Pages/RetroSynthesis/constants';
import { ButtonWithIcon } from '@els/els-react--button';
import { getSortDirectionText } from './RsTableSortControlUtils';

import './RsTableHeaderSortControl.scss';

export function RsTableSortControlPure({ children, onChange = () => null, sortParam, sortList }) {
  const [announcement, setAnnouncement] = useState('');
  const isInitialMountRef = useRef(true);

  const findSortState = (array, sortProperty) => {
    if (!array) {
      return null;
    }
    const { direction } = array.find((item) => item && item.property === sortProperty) || {};
    return direction;
  };

  const state = findSortState(sortList, sortParam);
  const normalizedState = state?.toLowerCase() || '';
  const isAsc = normalizedState === SortDirection.ASC.toLowerCase();
  const isDesc = normalizedState === SortDirection.DESC.toLowerCase();
  const nextDirection = isAsc ? SortDirection.DESC : SortDirection.ASC;
  let currentSprite = 'SortDefault';

  if (isAsc) {
    currentSprite = 'SortAsc';
  } else if (isDesc) {
    currentSprite = 'SortDesc';
  }

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (state) {
      setAnnouncement(`Table sorted by ${children} column in ${getSortDirectionText(state)} order.`);
    }
  }, [state, children]);

  useEffect(() => {
    if (!announcement) {
      return undefined;
    }

    const resetTimer = setTimeout(() => {
      setAnnouncement('');
    }, 600);

    return () => clearTimeout(resetTimer);
  }, [announcement]);

  const handleToggleClick = useCallback(() => {
    if (state !== nextDirection) {
      onChange(nextDirection);
    }
  }, [state, nextDirection, onChange]);

  return (
    <>
      <ButtonWithIcon
        data-e2e="sorted-toggle-button"
        sprite={currentSprite}
        type="link"
        className="rs-table-header-sort__button"
        iconRight
        onClick={handleToggleClick}>
        {children}
      </ButtonWithIcon>
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>
    </>
  );
}

RsTableSortControlPure.propTypes = {
  children: PropTypes.node,
  onChange: PropTypes.func,
  sortParam: PropTypes.string,
  sortList: PropTypes.arrayOf(
    PropTypes.shape({
      property: PropTypes.string,
      direction: PropTypes.oneOf(SortDirection.allTypes),
    })
  ),
};

// Wrapper around original SortControl to reduce some copy/paste
function RsTableHeaderSortControl({ sortList, sortParam, onChange = () => null, children }) {
  return (
    <RsTableSortControlPure
      sortList={sortList}
      sortParam={sortParam}
      onChange={(newValue) => onChange({ sort: `${sortParam},${newValue}` })}>
      {children}
    </RsTableSortControlPure>
  );
}

RsTableHeaderSortControl.propTypes = {
  sortList: PropTypes.arrayOf(
    PropTypes.shape({
      property: PropTypes.string,
      direction: PropTypes.oneOf(SortDirection.allTypes),
    })
  ),
  sortParam: PropTypes.string,
  onChange: PropTypes.func,
  children: PropTypes.node,
};

export default RsTableHeaderSortControl;
